class BasicHeader extends HTMLElement {
  constructor() {
    super();
  }

  get headerSection() {
    return document.querySelector('.header-section');
  }

  get enableTransparent() {
    return this.dataset.enableTransparent === 'true';
  }

  connectedCallback() {
    this.init();

    if (window.ResizeObserver) {
      new ResizeObserver(this.setHeight.bind(this)).observe(this);
    }

    if (Shopify.designMode) {
      const section = this.closest('.shopify-section');

      section.addEventListener(
        'shopify:section:load',
        this.init.bind(this)
      );

      section.addEventListener(
        'shopify:section:unload',
        this.init.bind(this)
      );

      section.addEventListener(
        'shopify:section:reorder',
        this.init.bind(this)
      );
    }
  }

  init() {
    new FoxTheme.delayUntilInteraction(
      this.setHeight.bind(this)
    );

    if (this.enableTransparent) {
      this.headerSection.classList.add('header-transparent');
    }
  }

  setHeight() {
    requestAnimationFrame(() => {
      const offsetHeight = Math.round(this.offsetHeight);
      const offsetTop = Math.round(this.parentElement.offsetTop);

      document.documentElement.style.setProperty(
        '--header-height',
        `${offsetHeight}px`
      );

      document.documentElement.style.setProperty(
        '--header-offset-top',
        `${offsetTop}px`
      );
    });
  }
}

customElements.define(
  'basic-header',
  BasicHeader,
  { extends: 'header' }
);


/* =========================================================
 * Sticky Header
 * ========================================================= */

class StickyHeader extends BasicHeader {
  constructor() {
    super();

    this.classes = {
      pinned: 'header-pinned',
    };
  }

  get isAlwaysSticky() {
    return this.dataset.stickyType === 'always';
  }

  connectedCallback() {
    super.connectedCallback();

    this.currentScrollTop = 0;
    this.firstScrollTop = window.scrollY;

    this.headerBounds =
      this.headerSection.getBoundingClientRect();

    this.beforeInit();
  }

  beforeInit() {
    this.headerSection.classList.add('header-sticky');

    this.headerSection.dataset.stickyType =
      this.dataset.stickyType;

    window.addEventListener(
      'scroll',
      this.onScrollHandler.bind(this),
      false
    );
  }

  onScrollHandler() {
    const scrollTop = window.scrollY;
    const headerSection = this.headerSection;

    const headerBoundsTop =
      this.headerBounds.top + this.firstScrollTop;

    const headerBoundsBottom =
      this.headerBounds.bottom + this.firstScrollTop;

    requestAnimationFrame(() => {
      if (scrollTop > headerBoundsTop) {
        headerSection.classList.add('header-scrolled');

        if (this.isAlwaysSticky) {
          document.body.classList.add(
            this.classes.pinned
          );
        } else {
          if (
            scrollTop < this.currentScrollTop ||
            scrollTop < headerBoundsBottom + 100
          ) {
            document.body.classList.add(
              this.classes.pinned
            );
          } else {
            document.body.classList.remove(
              this.classes.pinned
            );
          }
        }
      } else {
        headerSection.classList.remove(
          'header-scrolled'
        );

        document.body.classList.remove(
          this.classes.pinned
        );
      }

      this.currentScrollTop = scrollTop;
    });
  }
}


/* =========================================================
 * Register Sticky Header
 * ========================================================= */

customElements.define(
  'sticky-header',
  StickyHeader,
  { extends: 'header' }
);


/* =========================================================
 * Details Dropdown
 * ========================================================= */

const clearDropdownCount = new WeakMap();

class DetailsDropdown extends HTMLDetailsElement {
  constructor() {
    super();

    this.classes = {
      bodyClass: 'has-dropdown-menu',
    };

    this.events = {
      handleAfterHide: 'menu:handleAfterHide',
      handleAfterShow: 'menu:handleAfterShow',
    };

    this.summaryElement = this.firstElementChild;
    this.contentElement = this.lastElementChild;

    this._open = this.hasAttribute('open');

    this.summaryElement.addEventListener(
      'click',
      this.handleSummaryClick.bind(this)
    );

    if (this.trigger === 'hover') {
      this.summaryElement.addEventListener(
        'focusin',
        (event) => {
          if (event.target === this.summaryElement) {
            this.open = true;
          }
        }
      );

      this.summaryElement.addEventListener(
        'focusout',
        (event) => {
          if (
            !this.contentElement.contains(
              event.relatedTarget
            )
          ) {
            this.open = false;
          }
        }
      );
    }

    this.handleOutsideClick =
      this.handleOutsideClick.bind(this);

    this.handleEscKeyPress =
      this.handleEscKeyPress.bind(this);

    this.handleFocusOut =
      this.handleFocusOut.bind(this);

    this.hoverTimer = null;

    this.detectHoverListener =
      this.detectHover.bind(this);

    this.addEventListener(
      'mouseenter',
      this.detectHoverListener.bind(this)
    );

    this.addEventListener(
      'mouseleave',
      this.detectHoverListener.bind(this)
    );
  }

  set open(value) {
    if (value !== this._open) {
      this._open = value;

      if (this.isConnected) {
        this.transition(value);
      } else {
        if (value) {
          this.setAttribute('open', '');
        } else {
          this.removeAttribute('open');
        }
      }
    }
  }

  get open() {
    return this._open;
  }

  get trigger() {
    if (FoxTheme.config.isTouch) {
      return 'click';
    }

    return (
      this.getAttribute('trigger') ||
      'click'
    );
  }

  get level() {
    if (this.hasAttribute('level')) {
      return this.getAttribute('level');
    }

    return 'top';
  }

  handleSummaryClick(event) {
    event.preventDefault();

    if (
      !FoxTheme.config.isTouch &&
      this.trigger === 'hover' &&
      this.summaryElement.hasAttribute('data-link')
    ) {
      window.location.href =
        this.summaryElement.getAttribute(
          'data-link'
        );
    } else {
      this.open = !this.open;
    }
  }

  async transition(value) {
    if (value) {
      clearDropdownCount.set(
        DetailsDropdown,
        clearDropdownCount.get(DetailsDropdown) + 1
      );

      document.body.classList.add(
        this.classes.bodyClass
      );

      this.setAttribute('open', '');

      this.summaryElement.setAttribute(
        'open',
        ''
      );

      setTimeout(() => {
        this.contentElement.setAttribute(
          'open',
          ''
        );
      }, 100);

      document.addEventListener(
        'click',
        this.handleOutsideClick
      );

      document.addEventListener(
        'keydown',
        this.handleEscKeyPress
      );

      document.addEventListener(
        'focusout',
        this.handleFocusOut
      );

      await this.showWithTransition();

      this.needsReverse();

      return FoxTheme.utils.waitForEvent(
        this,
        this.events.handleAfterShow
      );
    } else {
      clearDropdownCount.set(
        DetailsDropdown,
        clearDropdownCount.get(DetailsDropdown) - 1
      );

      document.body.classList.toggle(
        this.classes.bodyClass,
        clearDropdownCount.get(
          DetailsDropdown
        ) > 0
      );

      this.summaryElement.removeAttribute(
        'open'
      );

      this.contentElement.removeAttribute(
        'open'
      );

      document.removeEventListener(
        'click',
        this.handleOutsideClick
      );

      document.removeEventListener(
        'keydown',
        this.handleEscKeyPress
      );

      document.removeEventListener(
        'focusout',
        this.handleFocusOut
      );

      await this.hideWithTransition();

      if (!this.open) {
        this.removeAttribute('open');
      }

      return FoxTheme.utils.waitForEvent(
        this,
        this.events.handleAfterHide
      );
    }
  }

  async showWithTransition() {
    FoxTheme.Motion.animate(
      this.contentElement,
      {
        opacity: [0, 1],
        visibility: 'visible',
      },
      {
        duration:
          FoxTheme.config.motionReduced
            ? 0
            : 0.6,
        easing: [0.7, 0, 0.2, 1],
        delay:
          FoxTheme.config.motionReduced
            ? 0
            : 0.1,
      }
    );

    const translateY =
      this.level === 'top'
        ? '-105%'
        : '2rem';

    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      {
        transform: [
          `translateY(${translateY})`,
          'translateY(0)',
        ],
      },
      {
        duration:
          FoxTheme.config.motionReduced
            ? 0
            : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  async hideWithTransition() {
    FoxTheme.Motion.animate(
      this.contentElement,
      {
        opacity: 0,
        visibility: 'hidden',
      },
      {
        duration:
          FoxTheme.config.motionReduced
            ? 0
            : 0.3,
        easing: [0.7, 0, 0.2, 1],
      }
    );

    const translateY =
      this.level === 'top'
        ? '-105%'
        : '2rem';

    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      {
        transform:
          `translateY(${translateY})`,
      },
      {
        duration:
          FoxTheme.config.motionReduced
            ? 0
            : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  handleOutsideClick(event) {
    const isClickInside =
      this.contains(event.target);

    const isClickOnDetailsDropdown =
      event.target.closest('details')
      instanceof DetailsDropdown;

    if (
      !isClickInside &&
      !isClickOnDetailsDropdown
    ) {
      this.open = false;
    }
  }

  handleEscKeyPress(event) {
    if (event.code === 'Escape') {
      const targetMenu =
        event.target.closest(
          'details[open]'
        );

      if (targetMenu) {
        targetMenu.open = false;
      }
    }
  }

  handleFocusOut(event) {
    if (
      event.relatedTarget &&
      !this.contains(event.relatedTarget)
    ) {
      this.open = false;
    }
  }

  detectHover(event) {
    if (this.trigger === 'hover') {
      this.open =
        event.type === 'mouseenter';
    }
  }

  needsReverse() {
    const totalWidth =
      this.contentElement.offsetLeft +
      this.contentElement.clientWidth * 2;

    if (totalWidth > window.innerWidth) {
      this.contentElement.classList.add(
        'needs-reverse'
      );
    }
  }
}

customElements.define(
  'details-dropdown',
  DetailsDropdown,
  { extends: 'details' }
);

clearDropdownCount.set(
  DetailsDropdown,
  0
);


/* =========================================================
 * Details Mega
 * ========================================================= */

class DetailsMega extends DetailsDropdown {
  constructor() {
    super();

    if (Shopify.designMode) {
      this.addEventListener(
        'shopify:block:select',
        () => {
          this.open = true;
        }
      );

      this.addEventListener(
        'shopify:block:deselect',
        () => {
          this.open = false;
        }
      );
    }
  }

  async showWithTransition() {
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      {
        visibility: 'visible',
        transform: [
          'translateY(-100%)',
          'translateY(0)',
        ],
      },
      {
        duration:
          FoxTheme.config.motionReduced
            ? 0
            : 0.5,
        easing: [
          0.39,
          0.575,
          0.565,
          1.0,
        ],
      }
    ).finished;
  }

  async hideWithTransition() {
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      {
        visibility: 'hidden',
        transform: 'translateY(-100%)',
      },
      {
        duration:
          FoxTheme.config.motionReduced
            ? 0
            : 0.5,
        easing: [
          0.39,
          0.575,
          0.565,
          1.0,
        ],
      }
    ).finished;
  }
}

customElements.define(
  'details-mega',
  DetailsMega,
  { extends: 'details' }
);

/* =========================================================
 * Menu Drawer Details
 * ========================================================= */

class MenuDrawerDetails extends HTMLDetailsElement {
  constructor() {
    super();

    this.summaryElement = this.querySelector(':scope > summary');

    if (this.summaryElement) {
      this.summaryElement.addEventListener(
        'click',
        this.onSummaryClick.bind(this)
      );
    }

    this.handleBackClick =
      this.handleBackClick.bind(this);
  }

  get summary() {
    return this.querySelector(':scope > summary');
  }

  get submenu() {
    return this.querySelector(
      ':scope > .menu-drawer__submenu'
    );
  }

  get parent() {
    return (
      this.closest(
        '.menu-drawer__submenu[data-parent]'
      ) ||
      this.closest('[data-parent]')
    );
  }

  get closeButton() {
    return this.querySelector(
      ':scope > .menu-drawer__submenu > .menu-drawer__item-back-link'
    );
  }

  connectedCallback() {
    if (this.closeButton) {
      this.closeButton.removeEventListener(
        'click',
        this.handleBackClick
      );

      this.closeButton.addEventListener(
        'click',
        this.handleBackClick
      );
    }

    /*
     * 默认打开的一级 / 二级菜单
     */
    if (this.hasAttribute('open')) {
      this.classList.add('active');

      if (this.summary) {
        this.summary.setAttribute(
          'aria-expanded',
          'true'
        );
      }
    }
  }

  /*
   * ==========================================================
   * 点击菜单
   * ==========================================================
   */
  onSummaryClick(event) {
    event.preventDefault();

    /*
     * 已经 active 不重复处理
     */
    if (this.classList.contains('active')) {
      return;
    }

    const parent = this.parent;

    if (!parent) {
      return;
    }

    /*
     * 当前第一层滚动容器
     */
    const drawer =
      this.closest('menu-drawer');

    const firstLayerScroll =
      drawer?.querySelector(
        '.menu-drawer-level-1-scroll'
      );

    /*
     * 第一层回顶部
     */
    this.resetScroll(
      firstLayerScroll
    );

    /*
     * 当前 submenu 回顶部
     */
    const currentScroll =
      this.querySelector(
        ':scope > .menu-drawer__submenu > .v-scrollable'
      );

    this.resetScroll(
      currentScroll
    );

    /*
     * 打开当前 details
     */
    this.setAttribute(
      'open',
      ''
    );

    /*
     * 父层 active
     */
    parent.classList.add(
      'active'
    );

    /*
     * 当前 details active
     */
    this.classList.add(
      'active'
    );

    /*
     * aria
     */
    if (this.summary) {
      this.summary.setAttribute(
        'aria-expanded',
        'true'
      );
    }

    /*
     * 浏览器完成 layout 后再次归零
     */
    requestAnimationFrame(() => {
      this.resetScroll(
        firstLayerScroll
      );

      this.resetScroll(
        currentScroll
      );
    });
  }

  /*
   * ==========================================================
   * 返回
   * ==========================================================
   */
  handleBackClick(event) {
    event.preventDefault();
    event.stopPropagation();

    this.onCloseButtonClick();
  }

  onCloseButtonClick() {
    const parent = this.parent;

    /*
     * 当前层
     */
    const currentScroll =
      this.querySelector(
        ':scope > .menu-drawer__submenu > .v-scrollable'
      );

    this.resetScroll(
      currentScroll
    );

    /*
     * 第一层
     */
    const drawer =
      this.closest('menu-drawer');

    const firstLayerScroll =
      drawer?.querySelector(
        '.menu-drawer-level-1-scroll'
      );

    this.resetScroll(
      firstLayerScroll
    );

    /*
     * 当前层移除 active
     */
    this.classList.remove(
      'active'
    );

    /*
     * 父层恢复
     */
    if (parent) {
      parent.classList.remove(
        'active'
      );
    }

    /*
     * aria
     */
    if (this.summary) {
      this.summary.setAttribute(
        'aria-expanded',
        'false'
      );
    }

    /*
     * 关闭 details
     */
    this.removeAttribute(
      'open'
    );

    /*
     * 下一帧再清一次
     */
    requestAnimationFrame(() => {
      this.resetScroll(
        currentScroll
      );

      this.resetScroll(
        firstLayerScroll
      );
    });
  }

  /*
   * ==========================================================
   * Scroll Reset
   * ==========================================================
   */
  resetScroll(element) {
    if (!element) {
      return;
    }

    const oldBehavior =
      element.style.scrollBehavior;

    element.style.scrollBehavior =
      'auto';

    element.scrollTop = 0;
    element.scrollLeft = 0;

    if (
      typeof element.scrollTo ===
      'function'
    ) {
      element.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto',
      });
    }

    /*
     * 强制 layout
     */
    void element.offsetHeight;

    element.scrollTop = 0;
    element.scrollLeft = 0;

    element.style.scrollBehavior =
      oldBehavior;
  }

  /*
   * ==========================================================
   * 给 MenuDrawer 统一调用
   * ==========================================================
   */
  resetAllScrollPositions() {
    const drawer =
      this.closest('menu-drawer');

    if (!drawer) {
      return;
    }

    drawer
      .querySelectorAll(
        '.v-scrollable'
      )
      .forEach((element) => {
        this.resetScroll(element);
      });
  }

  /*
   * 保留兼容主题可能调用的方法
   */
  closeAnimation() {
    this.removeAttribute(
      'open'
    );

    this.classList.remove(
      'active'
    );
  }
}

customElements.define(
  'menu-drawer-details',
  MenuDrawerDetails,
  {
    extends: 'details',
  }
);

/* =========================================================
 * Menu Drawer
 * ========================================================= */

class MenuDrawer extends DrawerComponent {
  constructor() {
    super();
  }

  get menuItems() {
    if (!this._menuItems) {
      this._menuItems =
        this.querySelectorAll(
          '.menu-drawer__menus:not(.active) > li'
        );
    }

    return this._menuItems;
  }

  prepareToShow() {
    super.prepareToShow();

    setTimeout(() => {
      this.animateMenuItems();
    }, 300);
  }

  animateMenuItems() {
    FoxTheme.Motion.animate(
      this.menuItems,
      {
        transform: [
          'translateX(-20px)',
          'translateX(0)',
        ],
        opacity: [0, 1],
      },
      {
        duration: 0.6,
        easing: [
          0.075,
          0.82,
          0.165,
          1,
        ],
        delay:
          FoxTheme.Motion.stagger(
            0.1
          ),
      }
    ).finished.then(() => {
      this.menuItems.forEach(
        (item) => {
          item.removeAttribute(
            'style'
          );
        }
      );
    });
  }

  /*
   * 整个 Menu Drawer 关闭以后，
   * 把所有二级 layer 恢复到初始状态。
   */
handleAfterHide() {
  super.handleAfterHide();

  setTimeout(() => {
    /*
     * ==========================================
     * 普通 Menu Details
     * ==========================================
     */
    this.querySelectorAll(
      'details[is="menu-drawer-details"]'
    ).forEach((menu) => {
      if (
        typeof menu.resetAllScrollPositions ===
        'function'
      ) {
        menu.resetAllScrollPositions();
      }
    });

    /*
     * ==========================================
     * 所有滚动区域
     * ==========================================
     */
    this.querySelectorAll(
      '.v-scrollable'
    ).forEach((element) => {
      element.style.scrollBehavior =
        'auto';

      element.scrollTop = 0;
      element.scrollLeft = 0;

      if (
        typeof element.scrollTo ===
        'function'
      ) {
        element.scrollTo({
          top: 0,
          left: 0,
          behavior: 'auto',
        });
      }
    });

    /*
     * ==========================================
     * Product 独立二级层
     * ==========================================
     */
    if (
      this._menuDrawerProductNavigation
    ) {
      this
        ._menuDrawerProductNavigation
        .reset();
    }
  }, 400);
}
}

customElements.define(
  'menu-drawer',
  MenuDrawer
);

class MenuDrawerProductNavigation {
  constructor(drawer) {
    this.drawer = drawer;
    this.handleClick = this.handleClick.bind(this);
    this.drawer.addEventListener('click', this.handleClick);
    console.log('MenuDrawerProductNavigation 初始化');
  }

  handleClick(event) {
    const trigger = event.target.closest('[data-product-submenu-trigger]');
    if (trigger && this.drawer.contains(trigger)) {
      event.preventDefault();
      event.stopPropagation();
      const submenuId = trigger.getAttribute('data-product-submenu-id');
      this.open(submenuId);
      return;
    }

    const backButton = event.target.closest('[data-product-submenu-back]');
    if (backButton && this.drawer.contains(backButton)) {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      return;
    }
  }

  open(id) {
    if (!id) return;

    this.drawer.querySelectorAll('.menu-drawer-product-submenu.active').forEach((submenu) => {
      submenu.classList.remove('active');
      submenu.setAttribute('aria-hidden', 'true');
    });

    let submenu = null;
    try {
      submenu = this.drawer.querySelector(`[data-product-submenu][id="${CSS.escape(id)}"]`);
    } catch (error) {
      console.warn('Invalid submenu id:', id);
      return;
    }

    if (!submenu) {
      console.warn('submenu not found:', id);
      return;
    }

    // ★★★ 移到 #MenuDrawer 的直接子级 ★★★
    const drawerElement = document.getElementById('MenuDrawer');
    if (drawerElement && submenu.parentElement !== drawerElement) {
      drawerElement.appendChild(submenu);
    }

    const scroll = submenu.querySelector('.menu-drawer-product-submenu-scroll');
    this.resetScroll(scroll);

    submenu.classList.add('active');
    submenu.setAttribute('aria-hidden', 'false');

    this.drawer.querySelectorAll('[data-product-submenu-trigger]').forEach((trigger) => {
      trigger.setAttribute(
        'aria-expanded',
        trigger.getAttribute('data-product-submenu-id') === id ? 'true' : 'false'
      );
    });

    requestAnimationFrame(() => {
      this.resetScroll(scroll);
    });
  }

  close() {
    const submenu = this.drawer.querySelector('.menu-drawer-product-submenu.active');
    if (!submenu) return;

    const scroll = submenu.querySelector('.menu-drawer-product-submenu-scroll');
    this.resetScroll(scroll);

    submenu.classList.remove('active');
    submenu.setAttribute('aria-hidden', 'true');

    this.drawer.querySelectorAll('[data-product-submenu-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });

    requestAnimationFrame(() => {
      this.resetScroll(scroll);
    });
  }

  reset() {
    this.drawer.querySelectorAll('.menu-drawer-product-submenu').forEach((submenu) => {
      submenu.classList.remove('active');
      submenu.setAttribute('aria-hidden', 'true');
      const scroll = submenu.querySelector('.menu-drawer-product-submenu-scroll');
      this.resetScroll(scroll);
    });

    this.drawer.querySelectorAll('[data-product-submenu-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  resetScroll(element) {
    if (!element) return;
    element.style.scrollBehavior = 'auto';
    element.scrollTop = 0;
    element.scrollLeft = 0;
    if (typeof element.scrollTo === 'function') {
      element.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
    void element.offsetHeight;
    element.scrollTop = 0;
    element.scrollLeft = 0;
  }
}


/* =========================================================
 * Menu Product List
 * ========================================================= */

class MenuProductList extends HTMLElement {
  constructor() {
    super();

    this.initSlide();

    this.elementToFocus =
      this.querySelector('button');
  }

  get container() {
    return this.querySelector(
      '.swiper'
    );
  }

  get sliderPagination() {
    return this.querySelector(
      '.swiper-pagination'
    );
  }

  get sliderNext() {
    return this.querySelector(
      '.swiper-button-next'
    );
  }

  get sliderPrev() {
    return this.querySelector(
      '.swiper-button-prev'
    );
  }

  get numberOfColumns() {
    return parseInt(
      this.dataset.columns
    );
  }

  initSlide() {
    const slider =
      new FoxTheme.Carousel(
        this.container,
        {
          spaceBetween: 10,

          slidesPerView:
            this.numberOfColumns,

          loop: false,

          pagination: {
            el: this.sliderPagination,
            type: 'fraction',
          },

          navigation: {
            nextEl: this.sliderNext,
            prevEl: this.sliderPrev,
          },

          focusableElements:
            'input, select, option, textarea, button, video, label',
        }
      );

    slider.init();

    const focusableElements =
      FoxTheme.a11y.getFocusableElements(
        this
      );

    focusableElements.forEach(
      (element) => {
        element.addEventListener(
          'focusin',
          function () {
            const slide =
              element.closest(
                '.swiper-slide'
              );

            slide &&
              slider.slider.slideTo(
                slider.slider.slides.indexOf(
                  slide
                )
              );
          }
        );
      }
    );
  }
}

customElements.define(
  'menu-product-list',
  MenuProductList
);